import { Purchases } from "@revenuecat/purchases-capacitor";
import { ensureRevenueCatConfigured } from "@/utils/revenuecat";
import { getRevenueCatOfferingId } from "@/config/revenuecat";

/**
 * Tries to purchase using the platform-specific Offering packages first (best practice).
 * Falls back to Current Offering, then to purchasing the StoreProduct directly.
 */
export const purchaseRevenueCatProduct = async (opts: { productId: string }) => {
  const ensured = await ensureRevenueCatConfigured();
  if (!ensured.ok) {
    throw new Error(ensured.error || "Purchases must be configured before calling this function");
  }

  // 1) Preferred path: platform-specific offering -> package
  const offerings = await Purchases.getOfferings().catch(() => null);
  
  // Get the platform-specific offering (e.g., "Pro apple" for iOS, "default" for Android)
  const offeringId = getRevenueCatOfferingId();
  const offering = offeringId 
    ? offerings?.all?.[offeringId] ?? offerings?.current
    : offerings?.current;
  
  const packages = offering?.availablePackages ?? [];

  const pkg = packages.find((p: any) => {
    const id = p?.product?.identifier ?? p?.product?.productIdentifier;
    return id === opts.productId;
  });

  if (pkg) {
    return Purchases.purchasePackage({ aPackage: pkg });
  }

  // 2) Fallback path: purchase product directly (lets you test even when Offering packages are empty)
  const productsRes = await Purchases.getProducts({ productIdentifiers: [opts.productId] });
  const storeProduct = productsRes?.products?.[0];

  if (!storeProduct) {
    throw new Error(
      `App Store didn't return product "${opts.productId}". ` +
        `Double-check the Product ID in App Store Connect and that the product is enabled for sale.`,
    );
  }

  return Purchases.purchaseStoreProduct({ product: storeProduct });
};
